/** @jsxImportSource @emotion/react */
import {
  CellStatuses,
  CellValue,
  DetailedCellError,
  Group,
  IdeasFile,
  IdeasFileNotFound,
  RoiFrame,
  RowIdentifier,
} from "@inscopix/ideas-hyperformula";
import { captureException } from "@sentry/react";
import { useDataTableContext } from "../store/DataTableProvider";
import { shallow } from "zustand/shallow";
import { CellStatus } from "components/CellStatusEditor/CellStatusEditor.types";
import { isDefined } from "utils/isDefined";
import { DATA_TABLE_TOOLTIP_ID } from "../layout/DataTableTooltip";
import { BadgePlain } from "../plain/BadgePlain";
import { css } from "@emotion/react";

/**
 * Formats a cell value for display in row identifier badges.
 * @param value The cell value.
 * @returns The value displayed in row identifier badges.
 */
const formatCellValueAsIdentifierValue = (value: CellValue): string => {
  if (
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "string"
  ) {
    return value.toString();
  }

  if (value instanceof IdeasFile) {
    return value.attrs.name;
  }

  if (value instanceof RowIdentifier) {
    return `${value.tableKey}-${value.rowIndex}`;
  }

  if (value instanceof CellStatuses) {
    const counts: Record<CellStatus, number> = {
      [CellStatus.Accepted]: 0,
      [CellStatus.Rejected]: 0,
      [CellStatus.Undecided]: 0,
    };

    value.statuses.forEach((status) => {
      counts[status]++;
    });

    return Object.entries(counts)
      .map(([status, count]) => {
        const statusName = CellStatus[status as unknown as CellStatus];
        return `${statusName}: ${count}`;
      })
      .join(", ");
  }

  if (value instanceof RoiFrame) {
    return `${value.shapes.length} ROIs`;
  }

  if (value instanceof Group) {
    return value.cellValues.map(formatCellValueAsIdentifierValue).join(",");
  }

  if (
    value instanceof DetailedCellError ||
    value === null ||
    value === IdeasFileNotFound
  ) {
    return "";
  }

  // Since we cannot use a switch statement here, we check that the
  // type of value is never. This ensures that when the CellValue type
  // changes, compiler errors are thrown and this function is updated.

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const exhaustiveCheck: never = value;
  captureException(
    "Exhaustive check failed in convertCellValueToIdentifierValue",
  );
  return "";
};

interface CellValueRendererRowIdentifierProps {
  rowIdentifier: RowIdentifier;
}

/** Component that renders a `RowIdentifier` cell value` */
export const CellValueRendererRowIdentifier = ({
  rowIdentifier,
}: CellValueRendererRowIdentifierProps) => {
  const { tableKey, rowIndex } = rowIdentifier;

  // Get the cell values in column with identifier position specified
  const additionalValues = useDataTableContext((s) => {
    const table = s.tables.find(({ key }) => key === tableKey);
    const row = table?.rows.find(({ index }) => index === rowIndex);
    const values: { columnName: string; value: string }[] = [];

    row?.cells.forEach((cell, colIdx) => {
      const column = table?.columns[colIdx];
      if (isDefined(column) && column.identifier_position !== null) {
        values[column.identifier_position] = {
          columnName: column.name,
          value: formatCellValueAsIdentifierValue(cell.value),
        };
      }
    });

    return values.filter(({ value }) => value !== "");
  }, shallow);

  // Create a list of column name and values to display in the tooltip
  const tooltipContent = (() => {
    if (additionalValues.length === 0) {
      return [];
    } else {
      return additionalValues.map(
        ({ columnName, value }) => `${columnName}: ${value}`,
      );
    }
  })();

  return (
    <div
      data-tooltip-id={DATA_TABLE_TOOLTIP_ID}
      data-tooltip-html={tooltipContent.join("<br />")}
      // space badges after the first one
      css={css`
        > * + * {
          margin-inline-start: 4px;
        }
      `}
    >
      <BadgePlain color="#ddd" borderColor="grey" fontFamily="monospace">
        {tableKey}-{rowIndex}
      </BadgePlain>

      {additionalValues.map(({ columnName, value }) => (
        <BadgePlain
          key={columnName}
          color="white"
          borderColor="grey"
          fontFamily="monospace"
        >
          {value}
        </BadgePlain>
      ))}
    </div>
  );
};
